import { useState, useRef } from 'react';
import { Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import { FaUpload, FaTrash, FaCopy, FaStar, FaRegStar, FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useUploadBlogImageMutation, useDeleteBlogImageMutation } from '../slices/blogsApiSlice';

export default function ImageUploader({ blogPostId, images, featuredImage, onImagesChange, onFeaturedChange, textareaRef }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const [uploadApi] = useUploadBlogImageMutation();
  const [deleteApi] = useDeleteBlogImageMutation();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image must be under 5 MB');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    if (blogPostId) formData.append('blogPostId', blogPostId);

    setUploading(true);
    try {
      const result = await uploadApi(formData).unwrap();
      onImagesChange([...images, result]);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await deleteApi(imageId).unwrap();
      onImagesChange(images.filter((img) => img._id !== imageId));
      if (featuredImage === imageId) onFeaturedChange(null);
      toast.success('Image deleted');
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed');
    }
  };

  const getMarkdown = (image) => `![${image.filename}](/api/blog/images/${image._id})`;

  const copyMarkdown = (image) => {
    navigator.clipboard.writeText(getMarkdown(image));
    toast.info('Markdown copied — paste with Ctrl+V');
  };

  const insertMarkdown = (image) => {
    const ta = textareaRef?.current;
    if (!ta) {
      copyMarkdown(image);
      return;
    }
    ta.focus();
    document.execCommand('insertText', false, getMarkdown(image));
    toast.success('Image inserted into content');
  };

  return (
    <Card className='mb-3 card--opaque'>
      <Card.Header className='d-flex justify-content-between align-items-center'>
        <span className='fw-bold'>Images</span>
        <Button
          size='sm'
          variant='outline-primary'
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Spinner size='sm' className='me-1' /> : <FaUpload className='me-1' />}
          Upload
        </Button>
        <input
          ref={fileRef}
          type='file'
          accept='image/jpeg,image/png,image/webp,image/gif'
          onChange={handleUpload}
          style={{ display: 'none' }}
        />
      </Card.Header>
      <Card.Body>
        <p className='text-muted small mb-2'>
          Click <strong>Insert</strong> to add an image at the cursor position, or <strong>Copy</strong> to copy the markdown and paste it manually.
        </p>
        {images.length > 0 && (
          <Row className='g-2'>
            {images.map((img) => (
              <Col key={img._id} xs={6} md={4} lg={3}>
                <Card className='h-100 card--opaque'>
                  <img
                    src={`/api/blog/images/${img._id}`}
                    alt={img.filename}
                    style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                  />
                  <Card.Body className='p-2 d-flex flex-wrap justify-content-between gap-1'>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      title='Insert image at cursor'
                      onClick={() => insertMarkdown(img)}
                    >
                      <FaArrowRight className='me-1' />Insert
                    </Button>
                    <Button
                      size='sm'
                      variant='outline-secondary'
                      title='Copy markdown'
                      onClick={() => copyMarkdown(img)}
                    >
                      <FaCopy />
                    </Button>
                    <Button
                      size='sm'
                      variant={featuredImage === img._id ? 'warning' : 'outline-warning'}
                      title={featuredImage === img._id ? 'Featured image' : 'Set as featured'}
                      onClick={() => onFeaturedChange(featuredImage === img._id ? null : img._id)}
                    >
                      {featuredImage === img._id ? <FaStar /> : <FaRegStar />}
                    </Button>
                    <Button
                      size='sm'
                      variant='outline-danger'
                      title='Delete image'
                      onClick={() => handleDelete(img._id)}
                    >
                      <FaTrash />
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
